import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DECISION_LABELS, TASK_META, type LabelValue, type Task } from '@/lib/model-lab'

interface DueRow {
  readonly id: string
  readonly step_label: string
}

interface Props {
  readonly task: Task
  readonly label: LabelValue | undefined
  readonly notes: string | null
  /** Gate only: the due rows, in decision order. */
  readonly dueRows: readonly DueRow[]
  readonly saving: boolean
  readonly onSave: (label: LabelValue, notes: string | null) => void
  readonly onClear: () => void
}

/**
 * The admin's answer for this case. Analysis/grace pick one decision (plus an
 * optional correct price); the gate picks send/skip per due row. SMS is rated
 * per model on each result card, so only notes live here.
 *
 * Initial state comes from the stored label; the parent remounts this (via
 * `key`) when the stored label changes.
 */
export function LabelControls({ task, label, notes, dueRows, saving, onSave, onClear }: Props) {
  const [decision, setDecision] = useState(label?.decision ?? '')
  // Gate: one choice per due row; unchosen rows stay empty until picked.
  const [rowDecisions, setRowDecisions] = useState<string[]>(() => {
    const stored = label?.decision?.split('|') ?? []
    return dueRows.map((_, i) => stored[i] ?? '')
  })
  const [price, setPrice] = useState(label?.price == null ? '' : String(label.price))
  const [noteText, setNoteText] = useState(notes ?? '')

  const setRow = (index: number, value: string) =>
    setRowDecisions((prev) => prev.map((d, i) => (i === index ? value : d)))

  const save = () => {
    const parsedPrice = price.trim() === '' ? null : Number(price)
    const value: LabelValue =
      task === 'sms'
        ? { ratings: label?.ratings ?? {} }
        : task === 'gate'
          ? { decision: rowDecisions.join('|') }
          : { decision, ...(task === 'analysis' && parsedPrice !== null && Number.isFinite(parsedPrice) ? { price: parsedPrice } : {}) }
    onSave(value, noteText.trim() || null)
  }

  const complete = task === 'sms' || (task === 'gate' ? rowDecisions.every(Boolean) : Boolean(decision))

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Correct answer</Label>
        {label && (
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={onClear} disabled={saving}>
            Clear label
          </Button>
        )}
      </div>

      {(task === 'analysis' || task === 'grace') && (
        <div className="flex flex-wrap gap-1.5">
          {TASK_META[task].decisions.map((d) => (
            <Button key={d} size="sm" variant={decision === d ? 'default' : 'outline'} onClick={() => setDecision(d)}>
              {DECISION_LABELS[d] ?? d}
            </Button>
          ))}
        </div>
      )}

      {task === 'analysis' && (
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Correct price (optional)</Label>
          <Input className="h-8 w-28" inputMode="decimal" value={price} placeholder="$" onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))} />
        </div>
      )}

      {task === 'gate' && (
        <div className="space-y-1.5">
          {dueRows.map((row, index) => (
            <div key={row.id} className="flex items-center gap-2 text-sm">
              <span className="w-36 font-mono text-xs">{row.step_label}</span>
              {(['send', 'skip'] as const).map((d) => (
                <Button key={d} size="sm" variant={rowDecisions[index] === d ? 'default' : 'outline'} className="h-7" onClick={() => setRow(index, d)}>
                  {DECISION_LABELS[d]}
                </Button>
              ))}
            </div>
          ))}
        </div>
      )}

      {task === 'sms' && <p className="text-xs text-muted-foreground">Rate each model's text with the thumbs on its card.</p>}

      <Textarea rows={2} placeholder="Notes (why this is the right answer)" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
      <Button size="sm" onClick={save} disabled={!complete || saving}>
        Save label
      </Button>
    </div>
  )
}
